import React,{useState,useMemo}from"react";
import{Bug,Wrench,Zap,Settings2,Search,Plus,ChevronRight,X,Layers,Edit2,Trash2,AlertTriangle}from"lucide-react";
import{BLUE,GOLD,ISSUE_TYPE_META,ISSUE_SEVERITY_META,ISSUE_STATUS_META,ISSUE_MILESTONES,dispName,fmtDate}from"../config";

// ── Badges ────────────────────────────────────────────────────────────────
function TypeBadge({type}){
  const m=ISSUE_TYPE_META[type]||{label:type||"Unknown",color:"#64748B",bg:"#F1F5F9"};
  return<span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{background:m.bg,color:m.color}}>{m.label}</span>;
}
function SevBadge({sev}){
  const m=ISSUE_SEVERITY_META[sev]||{label:sev||"?",color:"#64748B",bg:"#F1F5F9"};
  return<span className="text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap" style={{background:m.bg,color:m.color,borderColor:m.color+"44"}}>{m.label}</span>;
}
function StatusBadge({status}){
  const m=ISSUE_STATUS_META[status]||{label:status||"?",color:"#64748B",bg:"#F1F5F9"};
  return<span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{background:m.bg,color:m.color}}>{m.label}</span>;
}
function MilestoneBadge({milestone}){
  const colors={Now:"#DC2626",Next:"#D97706",Soon:"#2563EB",Backlog:"#64748B",Icebox:"#94A3B8"};
  const c=colors[milestone]||"#64748B";
  return<span className="text-xs font-medium px-2 py-0.5 rounded border whitespace-nowrap" style={{borderColor:c+"55",color:c}}>{milestone}</span>;
}

// ── Issue row ─────────────────────────────────────────────────────────────
function IssueRow({issue,selected,onSelect,products=[],allUsers=[]}){
  const prod=products.find(p=>p.spId===issue.product);
  return(
    <div onClick={onSelect} className={"flex items-center gap-3 bg-white border rounded-xl px-4 py-3 cursor-pointer transition-all hover:shadow-sm "+(selected?"border-blue-300":"border-slate-200")} style={selected?{boxShadow:"0 0 0 3px "+BLUE+"22"}:{}}>
      <span className="text-xs font-mono text-slate-300 w-8 shrink-0 text-center">#{issue.spId||"—"}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <TypeBadge type={issue.issueType}/>
          <SevBadge sev={issue.severity}/>
          <span className="text-sm font-semibold text-slate-800 truncate">{issue.title}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={issue.status}/>
          {issue.milestone&&<MilestoneBadge milestone={issue.milestone}/>}
          {prod&&<span className="text-xs text-indigo-600 font-medium flex items-center gap-1"><Layers size={9}/>{prod.projectName}</span>}
          {issue.own&&<span className="text-xs text-slate-400">{dispName(issue.own,allUsers)}</span>}
          {issue.productVersion&&<span className="text-xs text-slate-400 font-mono">v{issue.productVersion}</span>}
        </div>
      </div>
      <ChevronRight size={14} className="text-slate-300 shrink-0"/>
    </div>
  );
}

// ── Issue detail panel ────────────────────────────────────────────────────
function IssueDetail({issue,onClose,onEdit,onDelete,products=[],allUsers=[],cu}){
  if(!issue)return null;
  const prod=products.find(p=>p.spId===issue.product);
  const ptRole=cu?.productTrackRole||"none";
  const isOwner=issue.own?.toLowerCase()===cu?.email?.toLowerCase();
  const isReporter=issue.reporterEmail?.toLowerCase()===cu?.email?.toLowerCase();
  const canEdit=["admin","manager"].includes(ptRole)||isOwner||isReporter;
  const canDelete=ptRole==="admin";
  return(
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 shrink-0">
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"><X size={16}/></button>
        <span className="text-xs text-slate-400 font-mono shrink-0">#{issue.spId}</span>
        <TypeBadge type={issue.issueType}/>
        <div className="flex-1"/>
        {canEdit&&<button onClick={()=>onEdit(issue)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 shrink-0"><Edit2 size={11}/>Edit</button>}
        {canDelete&&<button onClick={()=>onDelete(issue)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 shrink-0"><Trash2 size={11}/>Delete</button>}
      </div>
      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <h2 className="text-base font-bold text-slate-800 leading-snug">{issue.title}</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={issue.status}/>
          <SevBadge sev={issue.severity}/>
          {issue.milestone&&<MilestoneBadge milestone={issue.milestone}/>}
        </div>
        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm bg-slate-50 rounded-xl p-3">
          <div><p className="text-xs text-slate-400 mb-0.5">Product</p><p className="font-medium text-slate-700">{prod?.projectName||"—"}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Source</p><p className="font-medium text-slate-700 capitalize">{issue.source||"—"}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Found In</p><p className="font-mono text-slate-700">{issue.productVersion?"v"+issue.productVersion:"—"}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Target Fix</p><p className="font-mono text-slate-700">{issue.targetVersion?"v"+issue.targetVersion:"—"}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Assigned To</p><p className="font-medium text-slate-700">{issue.own?dispName(issue.own,allUsers):"Unassigned"}</p></div>
          <div><p className="text-xs text-slate-400 mb-0.5">Reported By</p><p className="font-medium text-slate-700">{issue.reporterEmail?dispName(issue.reporterEmail,allUsers):"—"}</p></div>
          {issue.createdDate&&<div><p className="text-xs text-slate-400 mb-0.5">Reported</p><p className="font-medium text-slate-700">{fmtDate(issue.createdDate)}</p></div>}
          {issue.closedDate&&<div><p className="text-xs text-slate-400 mb-0.5">Closed</p><p className="font-medium text-slate-700">{fmtDate(issue.closedDate)}</p></div>}
        </div>
        {issue.desc&&<div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Description</p><p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{issue.desc}</p></div>}
        {issue.steps&&<div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Steps to Reproduce</p><pre className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3 whitespace-pre-wrap font-mono text-xs leading-relaxed overflow-x-auto">{issue.steps}</pre></div>}
        {issue.resolution&&<div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Resolution</p><p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{issue.resolution}</p></div>}
        {issue.notes&&<div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Notes</p><p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{issue.notes}</p></div>}
        {issue.tags?.length>0&&<div className="flex flex-wrap gap-1">{issue.tags.map(t=><span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>)}</div>}
      </div>
    </div>
  );
}

// ── Create/Edit modal ─────────────────────────────────────────────────────
function IssueModal({issue,products=[],allUsers=[],cu,onSave,onClose}){
  const isEdit=!!issue;
  const[form,setForm]=useState({
    title:issue?.title||"",
    issueType:issue?.issueType||"bug_sw",
    severity:issue?.severity||"medium",
    status:issue?.status||"new",
    milestone:issue?.milestone||"Backlog",
    product:issue?.product||"",
    productVersion:issue?.productVersion||"",
    targetVersion:issue?.targetVersion||"",
    source:issue?.source||"internal",
    desc:issue?.desc||"",
    steps:issue?.steps||"",
    resolution:issue?.resolution||"",
    notes:issue?.notes||"",
    own:issue?.own||"",
    reporterEmail:issue?.reporterEmail||cu?.email||"",
  });
  const[saving,setSaving]=useState(false);
  const set=k=>e=>setForm(f=>({...f,[k]:e.target.value}));
  const isBug=["bug_sw","bug_hw","discovered"].includes(form.issueType);
  const handleSave=async()=>{
    if(!form.title.trim())return;
    setSaving(true);
    try{await onSave(form);}
    finally{setSaving(false);}
  };
  return(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-base font-bold text-slate-800 flex-1">{isEdit?"Edit Issue":"New Issue"}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Title *</label>
            <input autoComplete="off" value={form.title} onChange={set("title")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-300" placeholder="Brief summary of the issue"/>
          </div>
          {/* Type + Severity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</label>
              <select value={form.issueType} onChange={set("issueType")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(ISSUE_TYPE_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Severity</label>
              <select value={form.severity} onChange={set("severity")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(ISSUE_SEVERITY_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          {/* Status + Milestone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</label>
              <select value={form.status} onChange={set("status")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(ISSUE_STATUS_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Milestone</label>
              <select value={form.milestone} onChange={set("milestone")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— None —</option>
                {ISSUE_MILESTONES.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          {/* Product + Versions */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</label>
              <select value={form.product} onChange={set("product")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— None —</option>
                {products.map(p=><option key={p.spId} value={p.spId}>{p.projectName}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Found In</label>
              <input autoComplete="off" value={form.productVersion} onChange={set("productVersion")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 2.1.4"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Target Fix</label>
              <input autoComplete="off" value={form.targetVersion} onChange={set("targetVersion")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 2.2.0"/>
            </div>
          </div>
          {/* Source + Owner */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</label>
              <select value={form.source} onChange={set("source")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="customer">Customer</option>
                <option value="internal">Internal</option>
                <option value="field">Field</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assign To</label>
              <select value={form.own} onChange={set("own")} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— Unassigned —</option>
                {allUsers.filter(u=>u.isActive).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"")).map(u=><option key={u.email} value={u.email}>{u.displayName||u.email}</option>)}
              </select>
            </div>
          </div>
          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</label>
            <textarea value={form.desc} onChange={set("desc")} rows={3} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-blue-300" placeholder="Full description of the issue or request"/>
          </div>
          {/* Steps (bugs only) */}
          {isBug&&<div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Steps to Reproduce</label>
            <textarea value={form.steps} onChange={set("steps")} rows={3} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:border-blue-300" placeholder={"1. Go to...\n2. Click on...\n3. Observe..."}/>
          </div>}
          {/* Resolution — edit only */}
          {isEdit&&<div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Resolution</label>
            <textarea value={form.resolution} onChange={set("resolution")} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-blue-300" placeholder="How was this resolved?"/>
          </div>}
          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-blue-300" placeholder="Additional notes or context"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={!form.title.trim()||saving} className="px-4 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-50 transition-opacity" style={{background:BLUE}}>{saving?"Saving…":isEdit?"Save Changes":"Create Issue"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ProductTrack view ────────────────────────────────────────────────
const OPEN_STATUSES=["new","triaged","in_progress","testing"];
const CLOSED_STATUSES=["resolved","closed","wont_fix","deferred"];

export function ProductTrack({issues=[],sel,setSel,cu,allUsers=[],projects=[],onCreateIssue,onEditIssue,onDeleteIssue,loading=false}){
  const[search,setSearch]=useState("");
  const[filterType,setFilterType]=useState("all");
  const[filterSev,setFilterSev]=useState("all");
  const[filterStatus,setFilterStatus]=useState("open");
  const[filterMilestone,setFilterMilestone]=useState("all");
  const[filterProduct,setFilterProduct]=useState("all");
  const[sortBy,setSortBy]=useState("rank");
  const[showModal,setShowModal]=useState(false);
  const[editingIssue,setEditingIssue]=useState(null);

  const products=projects.filter(p=>p.projectType==="product"&&p.isActive!==false);
  const ptRole=cu?.productTrackRole||"none";
  const canCreate=["admin","manager","contributor"].includes(ptRole);
  const selIssue=issues.find(i=>i.id===sel);

  const filtered=useMemo(()=>{
    let out=[...issues];
    if(filterStatus==="open") out=out.filter(i=>OPEN_STATUSES.includes(i.status));
    else if(filterStatus==="closed") out=out.filter(i=>CLOSED_STATUSES.includes(i.status));
    if(filterType!=="all") out=out.filter(i=>i.issueType===filterType);
    if(filterSev!=="all") out=out.filter(i=>i.severity===filterSev);
    if(filterMilestone!=="all") out=out.filter(i=>i.milestone===filterMilestone);
    if(filterProduct!=="all") out=out.filter(i=>i.product===filterProduct);
    if(search.trim()){const q=search.toLowerCase();out=out.filter(i=>i.title.toLowerCase().includes(q)||i.desc?.toLowerCase().includes(q)||i.notes?.toLowerCase().includes(q));}
    out.sort((a,b)=>{
      if(sortBy==="rank")return(a.priorityRank||999)-(b.priorityRank||999);
      if(sortBy==="severity"){const s={critical:0,high:1,medium:2,low:3};return(s[a.severity]??4)-(s[b.severity]??4);}
      if(sortBy==="status"){const s={new:0,triaged:1,in_progress:2,testing:3,deferred:4,resolved:5,closed:6,wont_fix:7};return(s[a.status]??8)-(s[b.status]??8);}
      if(sortBy==="created")return(b.createdDate||"").localeCompare(a.createdDate||"");
      return 0;
    });
    return out;
  },[issues,filterType,filterSev,filterStatus,filterMilestone,filterProduct,search,sortBy]);

  const openCount=issues.filter(i=>OPEN_STATUSES.includes(i.status)).length;
  const critCount=issues.filter(i=>i.severity==="critical"&&OPEN_STATUSES.includes(i.status)).length;

  const handleEdit=(issue)=>{setEditingIssue(issue);setShowModal(true);};
  const handleDelete=async(issue)=>{if(!window.confirm("Delete this issue permanently?"))return;await onDeleteIssue(issue);setSel(null);};
  const handleSave=async(data)=>{
    if(editingIssue)await onEditIssue({...editingIssue,...data});
    else await onCreateIssue(data);
    setShowModal(false);setEditingIssue(null);
  };

  return(
    <div className="flex h-full overflow-hidden">
      {/* Main list panel */}
      <div className={`flex flex-col flex-1 min-w-0 overflow-hidden${selIssue?" hidden md:flex":""}`}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div>
              <h1 className="text-base font-bold text-slate-800 flex items-center gap-2"><Bug size={16} style={{color:BLUE}}/>ProductTrack</h1>
              <p className="text-xs text-slate-400">Product issues, bugs &amp; enhancements</p>
            </div>
            <div className="flex-1"/>
            <div className="flex items-center gap-2">
              {critCount>0&&<span className="text-xs px-2 py-1 rounded-full font-bold text-white flex items-center gap-1" style={{background:"#DC2626"}}><AlertTriangle size={10}/>Critical: {critCount}</span>}
              <span className="text-xs text-slate-500 font-medium">{openCount} open</span>
            </div>
            {canCreate&&<button onClick={()=>{setEditingIssue(null);setShowModal(true);}} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg text-white font-medium shrink-0" style={{background:BLUE}}><Plus size={14}/>New Issue</button>}
          </div>
          {/* Filter bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input autoComplete="off" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" className="pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg w-36 focus:outline-none focus:border-blue-300"/>
            </div>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="all">All Status</option>
            </select>
            <select value={filterType} onChange={e=>setFilterType(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Types</option>
              {Object.entries(ISSUE_TYPE_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filterSev} onChange={e=>setFilterSev(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Severity</option>
              {Object.entries(ISSUE_SEVERITY_META).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filterMilestone} onChange={e=>setFilterMilestone(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Milestones</option>
              {ISSUE_MILESTONES.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
            {products.length>0&&<select value={filterProduct} onChange={e=>setFilterProduct(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Products</option>
              {products.map(p=><option key={p.spId} value={p.spId}>{p.projectName}</option>)}
            </select>}
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white ml-auto">
              <option value="rank">Priority Rank</option>
              <option value="severity">Severity</option>
              <option value="status">Status</option>
              <option value="created">Newest First</option>
            </select>
          </div>
        </div>
        {/* Issue list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading
            ?<p className="text-sm text-slate-400 text-center py-16">Loading issues…</p>
            :filtered.length===0
              ?<div className="text-center py-16">
                <Bug size={32} className="mx-auto mb-3 text-slate-200"/>
                <p className="text-sm text-slate-400">No issues match the current filters.</p>
                {canCreate&&<button onClick={()=>{setEditingIssue(null);setShowModal(true);}} className="mt-3 text-xs px-3 py-1.5 rounded-lg text-white" style={{background:BLUE}}>+ New Issue</button>}
              </div>
              :filtered.map(issue=>(
                <IssueRow key={issue.id} issue={issue} selected={sel===issue.id} onSelect={()=>setSel(sel===issue.id?null:issue.id)} products={products} allUsers={allUsers}/>
              ))
          }
        </div>
        {/* Footer count */}
        {filtered.length>0&&<div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-400 bg-white shrink-0">{filtered.length} issue{filtered.length!==1?"s":""} shown</div>}
      </div>

      {/* Detail panel */}
      {selIssue&&(
        <div className="fixed inset-0 z-40 bg-white flex flex-col overflow-hidden md:static md:inset-auto md:z-auto md:w-1/2 md:border-l md:border-slate-200">
          <IssueDetail issue={selIssue} onClose={()=>setSel(null)} onEdit={handleEdit} onDelete={handleDelete} products={products} allUsers={allUsers} cu={cu}/>
        </div>
      )}

      {/* Modal */}
      {showModal&&<IssueModal issue={editingIssue} products={products} allUsers={allUsers} cu={cu} onSave={handleSave} onClose={()=>{setShowModal(false);setEditingIssue(null);}}/>}
    </div>
  );
}
